// Knowledge base articles. Bodies use a tiny markdown subset: ## heading, - list, 1. list, > tip, ``` code, `code`, **bold**, [text](/link).
export const CATEGORIES = {
  start: { en: "Getting started", fi: "Aloitus" },
  deliver: { en: "Deliverability", fi: "Toimitettavuus" },
  dev: { en: "Developers", fi: "Kehittäjät" },
  marketing: { en: "Marketing campaigns", fi: "Markkinointikampanjat" },
  account: { en: "Account & compliance", fi: "Tili ja vaatimukset" },
};

export const GUIDES = [
  {
    slug: "quick-start", cat: "start", min: 5,
    title: { en: "Quick start: your first email in 10 minutes", fi: "Pika-aloitus: ensimmäinen sähköposti 10 minuutissa" },
    summary: { en: "Create an account, verify a domain, make an API key and send.", fi: "Luo tili, vahvista verkkotunnus, tee API-avain ja lähetä." },
    body: {
      en: `CARIO Mail sends email from **your own domain**, so receivers see your brand and mailbox providers can check the mail really comes from you.

## 1. Create your workspace
Sign up and confirm your email address. A workspace is created for you. Everything (domains, keys, contacts, messages) belongs to one workspace and is never visible to other customers.

## 2. Add and verify a sending domain
Open **Domains**, add a domain such as \`mail.yourbrand.com\` and copy the three DKIM CNAME records into your DNS provider. Verification usually finishes within an hour. See [Verify your sending domain](/guides/verify-domain).

## 3. Create an API key
Open **API keys** and create a key. Start with a \`cm_test_\` key: it runs every check but never sends real email, which makes it perfect for development.

## 4. Send
\`\`\`
curl -X POST https://YOUR_HOST/api/v1/emails \\
  -H "Authorization: Bearer cm_test_..." \\
  -H "Content-Type: application/json" \\
  -d '{"from":"Brand <hello@mail.yourbrand.com>","to":"you@example.com","subject":"Hello","text":"It works"}'
\`\`\`
Open **Messages** to see the result and its event history.

## 5. Go live
Switch to a \`cm_live_\` key. New accounts start in a **sandbox**: you can send to your own verified team members only, until you upgrade or support approves the account. See [Sandbox, limits and quotas](/guides/limits).

> Tip: add a webhook so your app hears about bounces and complaints automatically.`,
      fi: `CARIO Mail lähettää sähköpostia **omasta verkkotunnuksestasi**, joten vastaanottajat näkevät brändisi ja sähköpostipalvelut voivat tarkistaa, että viesti todella tulee sinulta.

## 1. Luo työtila
Rekisteröidy ja vahvista sähköpostiosoitteesi. Saat oman työtilan. Kaikki (verkkotunnukset, avaimet, yhteystiedot, viestit) kuuluu yhteen työtilaan, eikä se näy muille asiakkaille.

## 2. Lisää ja vahvista lähettävä verkkotunnus
Avaa **Verkkotunnukset**, lisää esimerkiksi \`mail.brandisi.fi\` ja kopioi kolme DKIM CNAME -tietuetta DNS-palveluusi. Vahvistus valmistuu yleensä tunnin sisällä. Katso [Vahvista lähettävä verkkotunnus](/guides/verify-domain).

## 3. Luo API-avain
Avaa **API-avaimet** ja luo avain. Aloita \`cm_test_\`-avaimella: se tekee kaikki tarkistukset mutta ei koskaan lähetä oikeaa postia, joten se sopii kehitykseen.

## 4. Lähetä
\`\`\`
curl -X POST https://YOUR_HOST/api/v1/emails \\
  -H "Authorization: Bearer cm_test_..." \\
  -H "Content-Type: application/json" \\
  -d '{"from":"Brändi <hei@mail.brandisi.fi>","to":"sinä@example.com","subject":"Hei","text":"Toimii"}'
\`\`\`
Avaa **Viestit** nähdäksesi tuloksen ja tapahtumahistorian.

## 5. Siirry tuotantoon
Vaihda \`cm_live_\`-avaimeen. Uudet tilit aloittavat **hiekkalaatikossa**: voit lähettää vain vahvistetuille tiimisi jäsenille, kunnes päivität paketin tai tuki hyväksyy tilin. Katso [Hiekkalaatikko, rajat ja kiintiöt](/guides/limits).

> Vinkki: lisää webhook, niin sovelluksesi saa tiedon palautuksista ja valituksista automaattisesti.`,
    },
  },
  {
    slug: "verify-domain", cat: "start", min: 6,
    title: { en: "Verify your sending domain (DKIM, SPF, DMARC)", fi: "Vahvista lähettävä verkkotunnus (DKIM, SPF, DMARC)" },
    summary: { en: "The DNS records that prove you own the domain and protect your reputation.", fi: "DNS-tietueet, jotka todistavat omistajuuden ja suojaavat mainettasi." },
    body: {
      en: `Mailbox providers trust mail that is **authenticated**. Three standards matter.

## DKIM (required)
DKIM signs every message with a key published in your DNS. When you add a domain, CARIO Mail shows three CNAME records like \`abc._domainkey.mail.yourbrand.com\`. Add all three exactly as shown. Use **Check DNS now** to see which records are already visible, then **Check with AWS** to finish verification.

## SPF
Amazon SES handles SPF on its own bounce domain, so you do not need to change SPF for DKIM-based sending. If you set up a custom MAIL FROM domain later, add the SPF record shown for it.

## DMARC (strongly recommended)
DMARC tells receivers what to do with mail that fails checks. Gmail and Yahoo require it for bulk senders. Start in monitor mode:
\`\`\`
_dmarc.yourbrand.com  TXT  "v=DMARC1; p=none; rua=mailto:dmarc@yourbrand.com"
\`\`\`
After a few weeks of clean reports, move to \`p=quarantine\` and later \`p=reject\`.

## Tips
- Use a subdomain (\`mail.\` or \`news.\`) so marketing reputation does not affect your main domain.
- Some DNS providers add your domain automatically: enter only \`abc._domainkey.mail\` as the name if the full name gets doubled.
- DNS changes can take up to 48 hours, but most finish within minutes.

> A domain can only be verified by one workspace at a time.`,
      fi: `Sähköpostipalvelut luottavat **todennettuun** postiin. Kolme standardia on tärkeitä.

## DKIM (pakollinen)
DKIM allekirjoittaa jokaisen viestin avaimella, joka on julkaistu DNS:ssäsi. Kun lisäät verkkotunnuksen, CARIO Mail näyttää kolme CNAME-tietuetta, kuten \`abc._domainkey.mail.brandisi.fi\`. Lisää kaikki kolme täsmälleen niin kuin ne näytetään. **Tarkista DNS nyt** näyttää, mitkä tietueet näkyvät jo, ja **Tarkista AWS:ltä** viimeistelee vahvistuksen.

## SPF
Amazon SES hoitaa SPF:n omalla palautusverkkotunnuksellaan, joten DKIM-pohjaisessa lähetyksessä SPF:ää ei tarvitse muuttaa. Jos myöhemmin otat käyttöön oman MAIL FROM -verkkotunnuksen, lisää sille näytetty SPF-tietue.

## DMARC (suositellaan vahvasti)
DMARC kertoo vastaanottajille, mitä tehdä tarkistuksissa epäonnistuneelle postille. Gmail ja Yahoo vaativat sen massapostittajilta. Aloita seurantatilassa:
\`\`\`
_dmarc.brandisi.fi  TXT  "v=DMARC1; p=none; rua=mailto:dmarc@brandisi.fi"
\`\`\`
Kun raportit ovat olleet puhtaita muutaman viikon, siirry \`p=quarantine\`- ja myöhemmin \`p=reject\`-tasolle.

## Vinkit
- Käytä aliverkkotunnusta (\`mail.\` tai \`news.\`), jotta markkinoinnin maine ei vaikuta päätunnukseesi.
- Jotkin DNS-palvelut lisäävät verkkotunnuksen automaattisesti: syötä nimeksi vain \`abc._domainkey.mail\`, jos koko nimi tuplaantuu.
- DNS-muutokset voivat kestää 48 tuntia, mutta useimmat valmistuvat minuuteissa.

> Verkkotunnuksen voi vahvistaa vain yksi työtila kerrallaan.`,
    },
  },
  {
    slug: "deliverability", cat: "deliver", min: 7,
    title: { en: "Deliverability checklist: reach the inbox", fi: "Toimitettavuuden tarkistuslista: perille postilaatikkoon" },
    summary: { en: "The habits that keep your mail out of spam folders.", fi: "Tavat, jotka pitävät postisi poissa roskapostikansiosta." },
    body: {
      en: `Inbox placement depends on **who you send to**, **what you send** and **how people react**.

## Before you send
- Authenticate: DKIM verified, DMARC published. See [Verify your sending domain](/guides/verify-domain).
- Send only to people who asked for your mail. Never buy, rent or scrape lists.
- Warm up new domains: start with your most engaged contacts and grow volume over 2–4 weeks.

## Content
- A clear From name people recognise, and a subject that matches the content.
- Always include a plain-text version and a working unsubscribe link (campaigns add one automatically).
- Avoid ALL CAPS, many exclamation marks, link shorteners and image-only emails.
- Use the **AI spam check** on templates to catch risky wording before you send.

## Keep your list clean
- Hard bounces, complaints and unsubscribes are suppressed automatically. Do not re-import them.
- Remove contacts who have not opened anything in 6–12 months.

## Know the thresholds
- Keep bounces under **2%** (we pause sending above 5%).
- Keep spam complaints under **0.1%** (we pause above 0.1% over 7 days). Gmail and Yahoo reject senders above 0.3%.

> Watch the Dashboard: bounce and complaint rates appear there as soon as delivery events are enabled.`,
      fi: `Postilaatikkoon pääsy riippuu siitä, **kenelle lähetät**, **mitä lähetät** ja **miten ihmiset reagoivat**.

## Ennen lähetystä
- Todenna: DKIM vahvistettu, DMARC julkaistu. Katso [Vahvista lähettävä verkkotunnus](/guides/verify-domain).
- Lähetä vain ihmisille, jotka ovat pyytäneet postiasi. Älä koskaan osta, vuokraa tai haravoi listoja.
- Lämmitä uudet verkkotunnukset: aloita aktiivisimmista yhteystiedoista ja kasvata määrää 2–4 viikon aikana.

## Sisältö
- Selkeä, tunnistettava lähettäjän nimi ja sisältöä vastaava otsikko.
- Liitä aina pelkkä teksti -versio ja toimiva tilauksen perumislinkki (kampanjat lisäävät sen automaattisesti).
- Vältä ISOJA KIRJAIMIA, monia huutomerkkejä, linkkien lyhentäjiä ja pelkkiä kuvia.
- Käytä mallipohjien **tekoälyroskapostitarkistusta** riskisanojen löytämiseen ennen lähetystä.

## Pidä lista puhtaana
- Pysyvät palautukset, valitukset ja perutut tilaukset estetään automaattisesti. Älä tuo niitä uudelleen.
- Poista yhteystiedot, jotka eivät ole avanneet mitään 6–12 kuukauteen.

## Tunne raja-arvot
- Pidä palautukset alle **2 %** (keskeytämme lähetyksen yli 5 %:ssa).
- Pidä roskapostivalitukset alle **0,1 %** (keskeytämme yli 0,1 %:ssa 7 päivän aikana). Gmail ja Yahoo hylkäävät lähettäjät yli 0,3 %:ssa.

> Seuraa Yleiskatsausta: palautus- ja valitusasteet näkyvät siellä heti, kun toimitustapahtumat ovat käytössä.`,
    },
  },
  {
    slug: "bounces-suppressions", cat: "deliver", min: 4,
    title: { en: "Bounces, complaints and the suppression list", fi: "Palautukset, valitukset ja estolista" },
    summary: { en: "Why some addresses are skipped and how to manage them.", fi: "Miksi joitain osoitteita ohitetaan ja miten niitä hallitaan." },
    body: {
      en: `The **suppression list** is your workspace's do-not-send list. Any address on it is skipped on every send, from the API, SMTP and campaigns.

## How addresses get there
- **Hard bounce**: the mailbox does not exist. Added automatically.
- **Complaint**: the person clicked "Report spam". Added automatically.
- **Unsubscribe**: the person used the unsubscribe link or their mail app's unsubscribe button.
- **Manual**: you added it under Suppressions.

## Soft bounces
Temporary problems (full mailbox, server busy) are retried with backoff and do not suppress the address.

## Removing an address
Admins can remove an address from **Suppressions**, for example after a typo was fixed. Never remove complaints or unsubscribes without the person's new, clear consent.

> The AI bounce explainer on each failed message tells you in plain words why it bounced and what to do.`,
      fi: `**Estolista** on työtilasi "älä lähetä" -lista. Listalla olevat osoitteet ohitetaan jokaisessa lähetyksessä: API, SMTP ja kampanjat.

## Miten osoitteet päätyvät listalle
- **Pysyvä palautus**: postilaatikkoa ei ole. Lisätään automaattisesti.
- **Valitus**: henkilö painoi "Ilmoita roskapostiksi". Lisätään automaattisesti.
- **Tilauksen peruminen**: henkilö käytti perumislinkkiä tai sähköpostisovelluksensa perumispainiketta.
- **Käsin**: lisäsit osoitteen Estolistalle itse.

## Tilapäiset palautukset
Tilapäiset ongelmat (täysi postilaatikko, kiireinen palvelin) yritetään uudelleen viiveellä, eikä osoitetta estetä.

## Osoitteen poistaminen
Ylläpitäjät voivat poistaa osoitteen **Estolistalta**, esimerkiksi kirjoitusvirheen korjaamisen jälkeen. Älä koskaan poista valituksia tai perumisia ilman henkilön uutta, selkeää suostumusta.

> Tekoälyn palautusselitys jokaisessa epäonnistuneessa viestissä kertoo selkokielellä, miksi viesti palautui ja mitä tehdä.`,
    },
  },
  {
    slug: "tracking", cat: "deliver", min: 4,
    title: { en: "Open and click tracking", fi: "Avausten ja klikkausten seuranta" },
    summary: { en: "How tracking works, when to use it and its limits.", fi: "Miten seuranta toimii, milloin sitä kannattaa käyttää ja sen rajat." },
    body: {
      en: `Tracking is **off by default**. Turn it on per domain under **Domains**, per campaign in the editor, or per email with \`track_opens\` / \`track_clicks\` in the API.

## How it works
- **Opens**: a tiny invisible image is added to the HTML. When the mail app loads it, an open is counted.
- **Clicks**: links are routed through CARIO Mail, counted and redirected instantly to the original address. Unsubscribe links are never rewritten.

## Webhooks
\`email.opened\` fires on the first open, \`email.clicked\` on the first click of each link.

## Limits to know
- Apple Mail Privacy Protection and some security scanners load images automatically, so open rates can look high.
- Mail apps that block images do not report opens at all.
- Click data is more reliable than opens.

> For receipts and password resets, leave tracking off: it adds nothing and keeps messages lean.`,
      fi: `Seuranta on **oletuksena pois päältä**. Ota se käyttöön verkkotunnuskohtaisesti kohdassa **Verkkotunnukset**, kampanjakohtaisesti editorissa tai viestikohtaisesti API:n \`track_opens\`- / \`track_clicks\`-kentillä.

## Miten se toimii
- **Avaukset**: HTML:ään lisätään pieni näkymätön kuva. Kun sähköpostisovellus lataa sen, avaus lasketaan.
- **Klikkaukset**: linkit ohjataan CARIO Mailin kautta, lasketaan ja ohjataan heti alkuperäiseen osoitteeseen. Perumislinkkejä ei koskaan muuteta.

## Webhookit
\`email.opened\` lähtee ensimmäisestä avauksesta, \`email.clicked\` kunkin linkin ensimmäisestä klikkauksesta.

## Rajoitukset
- Apple Mailin yksityisyyssuoja ja jotkin tietoturvaskannerit lataavat kuvat automaattisesti, joten avausaste voi näyttää korkealta.
- Kuvia estävät sovellukset eivät ilmoita avauksia lainkaan.
- Klikkaustieto on luotettavampaa kuin avaukset.

> Kuiteissa ja salasanan palautuksissa pidä seuranta pois päältä: se ei tuo lisäarvoa.`,
    },
  },
  {
    slug: "api-basics", cat: "dev", min: 6,
    title: { en: "REST API basics: keys, idempotency and errors", fi: "REST API:n perusteet: avaimet, idempotenssi ja virheet" },
    summary: { en: "Authenticate, retry safely and handle every response.", fi: "Todenna, yritä uudelleen turvallisesti ja käsittele vastaukset." },
    body: {
      en: `Every request uses \`Authorization: Bearer cm_live_...\`. Keys are shown once and stored only as a hash.

## Scopes
- \`emails:send\` – send email
- \`emails:read\` – read message status
- \`contacts:read\` / \`contacts:write\` – manage campaign audiences
Give each system only the scopes it needs, and optionally lock a key to one sending domain.

## Idempotency
Network failures happen. Send an \`Idempotency-Key\` header (for example \`order-1234-receipt\`). Repeating the same key with the same body returns the original email instead of sending twice. The same key with a different body returns 409.

## Status codes
- 202 accepted for sending · 200 idempotent replay
- 401 bad key · 403 missing scope, unverified domain or sandbox
- 413 too large · 422 validation error or every recipient suppressed
- 429 rate limit or quota (see \`Retry-After\`) · 503 sending paused

## Statuses
\`queued → accepted → delivered\`, or \`bounced\`, \`complained\`, \`failed\`. "Accepted" means the upstream server took the message; "delivered" comes only from real delivery events.

See the full [API reference](/docs).`,
      fi: `Jokainen pyyntö käyttää otsaketta \`Authorization: Bearer cm_live_...\`. Avaimet näytetään kerran ja tallennetaan vain tiivisteenä.

## Oikeudet
- \`emails:send\` – lähetä sähköpostia
- \`emails:read\` – lue viestien tila
- \`contacts:read\` / \`contacts:write\` – hallitse kampanjoiden yleisöjä
Anna jokaiselle järjestelmälle vain tarvittavat oikeudet ja lukitse avain halutessasi yhteen verkkotunnukseen.

## Idempotenssi
Verkkovirheitä sattuu. Lähetä \`Idempotency-Key\`-otsake (esim. \`tilaus-1234-kuitti\`). Sama avain samalla sisällöllä palauttaa alkuperäisen viestin eikä lähetä kahdesti. Sama avain eri sisällöllä palauttaa 409.

## Tilakoodit
- 202 hyväksytty lähetettäväksi · 200 idempotentti toisto
- 401 virheellinen avain · 403 puuttuva oikeus, vahvistamaton verkkotunnus tai hiekkalaatikko
- 413 liian suuri · 422 validointivirhe tai kaikki vastaanottajat estetty
- 429 nopeusraja tai kiintiö (katso \`Retry-After\`) · 503 lähetys keskeytetty

## Tilat
\`queued → accepted → delivered\` tai \`bounced\`, \`complained\`, \`failed\`. "Accepted" tarkoittaa, että vastaanottava palvelin otti viestin; "delivered" tulee vain todellisista toimitustapahtumista.

Katso koko [API-dokumentaatio](/docs).`,
    },
  },
  {
    slug: "smtp", cat: "dev", min: 3,
    title: { en: "Send through SMTP", fi: "Lähetä SMTP:n kautta" },
    summary: { en: "Connect WordPress, Shopify apps, CRMs and other tools.", fi: "Yhdistä WordPress, kauppa-alustat, CRM:t ja muut työkalut." },
    body: {
      en: `Many tools can only send through SMTP. Create an **SMTP key** under API keys and use:
- Port **587** with STARTTLS (or 465 with TLS)
- Username: \`cario\`
- Password: your SMTP key

SMTP mail passes through the same checks, suppressions, limits and logs as the API. Add \`X-Cario-Tags: key=value\` to tag messages.

Full setup steps for common tools are in the [SMTP guide](/docs/smtp).`,
      fi: `Monet työkalut osaavat lähettää vain SMTP:llä. Luo **SMTP-avain** kohdassa API-avaimet ja käytä:
- Portti **587** STARTTLS:llä (tai 465 TLS:llä)
- Käyttäjätunnus: \`cario\`
- Salasana: SMTP-avaimesi

SMTP-posti kulkee samojen tarkistusten, estojen, rajojen ja lokien läpi kuin API. Lisää \`X-Cario-Tags: avain=arvo\` merkitäksesi viestit.

Yleisten työkalujen asetusohjeet löytyvät [SMTP-oppaasta](/docs/smtp).`,
    },
  },
  {
    slug: "webhooks", cat: "dev", min: 5,
    title: { en: "Webhooks: react to email events", fi: "Webhookit: reagoi sähköpostitapahtumiin" },
    summary: { en: "Receive signed events and verify them safely.", fi: "Vastaanota allekirjoitettuja tapahtumia ja tarkista ne turvallisesti." },
    body: {
      en: `A webhook is an HTTPS address in your app that CARIO Mail calls when something happens.

## Events
\`email.accepted\`, \`email.delivered\`, \`email.bounced\`, \`email.complained\`, \`email.failed\`, \`email.opened\`, \`email.clicked\`, \`email.unsubscribed\`.

## Verify every request
Each call carries \`webhook-id\`, \`webhook-timestamp\` and \`webhook-signature\`. Compute HMAC-SHA256 over \`id.timestamp.body\` with your signing secret and compare. Reject anything older than 5 minutes.

## Reliability
- Reply with any 2xx within 10 seconds; do slow work in the background.
- Failed deliveries are retried with backoff. You can replay any delivery from the dashboard.
- Handle duplicates: use \`webhook-id\` to ignore repeats.

> Rotate the signing secret if it ever leaks; the old one stops working immediately.`,
      fi: `Webhook on sovelluksesi HTTPS-osoite, jota CARIO Mail kutsuu, kun jotain tapahtuu.

## Tapahtumat
\`email.accepted\`, \`email.delivered\`, \`email.bounced\`, \`email.complained\`, \`email.failed\`, \`email.opened\`, \`email.clicked\`, \`email.unsubscribed\`.

## Tarkista jokainen pyyntö
Jokaisessa kutsussa on \`webhook-id\`, \`webhook-timestamp\` ja \`webhook-signature\`. Laske HMAC-SHA256 merkkijonosta \`id.timestamp.body\` allekirjoitusavaimellasi ja vertaa. Hylkää yli 5 minuuttia vanhat.

## Luotettavuus
- Vastaa millä tahansa 2xx-koodilla 10 sekunnissa; tee hidas työ taustalla.
- Epäonnistuneet toimitukset yritetään uudelleen viiveellä. Voit toistaa minkä tahansa toimituksen hallintapaneelista.
- Käsittele kaksoiskappaleet: ohita toistot \`webhook-id\`:n avulla.

> Vaihda allekirjoitusavain, jos se vuotaa; vanha lakkaa toimimasta heti.`,
    },
  },
  {
    slug: "templates", cat: "dev", min: 4,
    title: { en: "Templates and variables", fi: "Mallipohjat ja muuttujat" },
    summary: { en: "Reusable designs with safe personalisation.", fi: "Uudelleenkäytettävät pohjat turvallisella personoinnilla." },
    body: {
      en: `Templates keep your design in one place. Write HTML and text with variables like \`{{first_name}}\`, then send with \`template_id\` and \`variables\`.

## Safe by default
Variable values are HTML-escaped, so customer data can never inject code into your email.

## AI helpers
- **Spam check** scores the template and names the main issue.
- **Tips** gives concrete fixes; **Rewrite** suggests a cleaner version you can review.

## Images
Upload logos and images under **Files** and insert them; they are served from a fast public link.`,
      fi: `Mallipohjat pitävät ulkoasusi yhdessä paikassa. Kirjoita HTML ja teksti muuttujilla, kuten \`{{first_name}}\`, ja lähetä kentillä \`template_id\` ja \`variables\`.

## Turvallinen oletuksena
Muuttujien arvot HTML-suojataan, joten asiakastiedot eivät voi lisätä koodia viestiin.

## Tekoälyapurit
- **Roskapostitarkistus** pisteyttää pohjan ja nimeää pääongelman.
- **Vinkit** antaa konkreettiset korjaukset; **Uudelleenkirjoitus** ehdottaa siistimmän version tarkistettavaksi.

## Kuvat
Lataa logot ja kuvat kohtaan **Tiedostot** ja lisää ne; ne tarjoillaan nopeasta julkisesta linkistä.`,
    },
  },
  {
    slug: "campaigns", cat: "marketing", min: 7,
    title: { en: "Run your first marketing campaign", fi: "Aja ensimmäinen markkinointikampanjasi" },
    summary: { en: "From audience to report: draft, test, schedule, measure.", fi: "Yleisöstä raporttiin: luonnos, testi, ajastus, mittaus." },
    body: {
      en: `Campaigns send one personalised email to every contact in an audience, from your verified domain.

## 1. Build an audience
Open **Contacts**, create an audience and import a CSV or add people by hand. See [Import contacts from CSV](/guides/import-contacts).

## 2. Create the campaign
Open **Campaigns → New campaign**. Choose the audience and a From address on a verified domain (for example \`Brand <news@mail.yourbrand.com>\`).

## 3. Write it
Click **Write with AI**, describe the email in a sentence or two and pick a tone and language. You get a subject, an alternative subject, preview text, HTML and plain text. Replace any \`[placeholders]\` with real facts. Personalise with \`{{first_name}}\` or any CSV column.

## 4. Test it
**Send test** delivers a real email marked [Test] to you (or up to 3 addresses), personalised with the first contact's details.

## 5. Send or schedule
Send now, or pick a date and time up to 90 days ahead. Unschedule any time to edit.

## 6. Read the report
The campaign page shows progress, accepted, opens, clicks, bounces and unsubscribes, refreshing while it sends.

> Every campaign email gets a visible unsubscribe link and one-click unsubscribe headers. People who unsubscribe are skipped forever.`,
      fi: `Kampanja lähettää yhden personoidun viestin jokaiselle yleisön yhteystiedolle vahvistetusta verkkotunnuksestasi.

## 1. Rakenna yleisö
Avaa **Yhteystiedot**, luo yleisö ja tuo CSV tai lisää ihmisiä käsin. Katso [Tuo yhteystiedot CSV:stä](/guides/import-contacts).

## 2. Luo kampanja
Avaa **Kampanjat → Uusi kampanja**. Valitse yleisö ja lähettäjäosoite vahvistetusta verkkotunnuksesta (esim. \`Brändi <uutiset@mail.brandisi.fi>\`).

## 3. Kirjoita
Napsauta **Write with AI**, kuvaile viesti lauseella tai kahdella ja valitse sävy ja kieli. Saat otsikon, vaihtoehtoisen otsikon, esikatselutekstin, HTML:n ja pelkän tekstin. Korvaa \`[paikkamerkit]\` oikeilla tiedoilla. Personoi \`{{first_name}}\`-muuttujalla tai millä tahansa CSV-sarakkeella.

## 4. Testaa
**Send test** lähettää oikean, [Test]-merkityn viestin sinulle (tai enintään 3 osoitteeseen) ensimmäisen yhteystiedon tiedoilla.

## 5. Lähetä tai ajasta
Lähetä heti tai valitse päivä ja aika enintään 90 päivän päähän. Voit perua ajastuksen milloin tahansa muokataksesi.

## 6. Lue raportti
Kampanjasivu näyttää edistymisen, hyväksytyt, avaukset, klikkaukset, palautukset ja perumiset, ja päivittyy lähetyksen aikana.

> Jokaisessa kampanjaviestissä on näkyvä perumislinkki ja yhden klikkauksen perumisotsakkeet. Perujat ohitetaan pysyvästi.`,
    },
  },
  {
    slug: "import-contacts", cat: "marketing", min: 4,
    title: { en: "Import contacts from CSV", fi: "Tuo yhteystiedot CSV:stä" },
    summary: { en: "File format, custom fields and consent.", fi: "Tiedostomuoto, omat kentät ja suostumus." },
    body: {
      en: `Export your list from a spreadsheet as **CSV (comma separated, UTF-8)**.

## Format
\`\`\`
email,first_name,last_name,city,plan
ada@example.com,Ada,Lovelace,Helsinki,pro
linus@example.com,Linus,,Espoo,free
\`\`\`
- \`email\` is required. \`first_name\` and \`last_name\` are optional.
- Up to 20 extra columns become fields you can use as \`{{city}}\` or \`{{plan}}\`. Column names are lowercased and spaces become \`_\`.
- Up to 20,000 rows and 1 MB per file; split bigger lists.
- Importing an email that already exists updates it instead of duplicating it.
- Invalid addresses are skipped and listed by row number.

## Consent
You must confirm everyone agreed to hear from you. Bought or scraped lists damage deliverability for everyone and lead to account suspension.

## Sync from your system
Use the contacts API with a \`contacts:write\` key to add people the moment they sign up. See the [API reference](/docs).`,
      fi: `Vie listasi taulukkolaskennasta muodossa **CSV (pilkuilla eroteltu, UTF-8)**.

## Muoto
\`\`\`
email,first_name,last_name,city,plan
ada@example.com,Ada,Lovelace,Helsinki,pro
linus@example.com,Linus,,Espoo,free
\`\`\`
- \`email\` on pakollinen. \`first_name\` ja \`last_name\` ovat valinnaisia.
- Enintään 20 lisäsaraketta muuttuu kentiksi, joita voit käyttää muodossa \`{{city}}\` tai \`{{plan}}\`. Sarakkeiden nimet muutetaan pieniksi kirjaimiksi ja välilyönnit muuttuvat merkiksi \`_\`.
- Enintään 20 000 riviä ja 1 Mt tiedostoa kohden; jaa suuremmat listat.
- Jo olemassa olevan osoitteen tuonti päivittää sen eikä luo kaksoiskappaletta.
- Virheelliset osoitteet ohitetaan ja listataan rivinumeroittain.

## Suostumus
Sinun on vahvistettava, että jokainen on suostunut postiisi. Ostetut tai haravoidut listat heikentävät kaikkien toimitettavuutta ja johtavat tilin jäädyttämiseen.

## Synkronoi omasta järjestelmästä
Käytä yhteystieto-API:a \`contacts:write\`-avaimella lisätäksesi ihmiset heti rekisteröitymisen yhteydessä. Katso [API-dokumentaatio](/docs).`,
    },
  },
  {
    slug: "split-testing", cat: "marketing", min: 3,
    title: { en: "Split test subject lines", fi: "Otsikoiden A/B-testaus" },
    summary: { en: "Let your audience pick the better subject.", fi: "Anna yleisösi valita parempi otsikko." },
    body: {
      en: `A split test sends two subject lines to a small test group, waits, then sends the winner to everyone else.

## Set it up
Turn on **Split test two subject lines**, write Subject B, and choose:
- **Test group %** (10–50%): half get A, half get B.
- **Wait** (1–48 hours): how long to collect opens.

The subject with the higher open rate wins; a tie goes to A. Open tracking is switched on automatically for split tests.

## Good tests
- Change one thing: length, a question vs a statement, with or without the name.
- Use at least a few hundred contacts in the test group for a meaningful result.
- 4 hours suits most audiences; use 24 hours for B2B lists that read mail during work hours.`,
      fi: `A/B-testi lähettää kaksi otsikkoa pienelle testiryhmälle, odottaa ja lähettää voittajan kaikille muille.

## Asetukset
Ota käyttöön **Split test two subject lines**, kirjoita otsikko B ja valitse:
- **Testiryhmä %** (10–50 %): puolet saa A:n, puolet B:n.
- **Odotus** (1–48 tuntia): kuinka kauan avauksia kerätään.

Korkeamman avausasteen otsikko voittaa; tasapelissä A voittaa. Avausten seuranta kytketään automaattisesti päälle A/B-testeissä.

## Hyvät testit
- Muuta yhtä asiaa: pituus, kysymys vai toteamus, nimen kanssa tai ilman.
- Käytä testiryhmässä vähintään muutamaa sataa yhteystietoa luotettavan tuloksen saamiseksi.
- 4 tuntia sopii useimmille; B2B-listoille 24 tuntia.`,
    },
  },
  {
    slug: "unsubscribe-compliance", cat: "account", min: 5,
    title: { en: "Unsubscribe, consent and GDPR", fi: "Tilauksen peruminen, suostumus ja GDPR" },
    summary: { en: "What the law and Gmail/Yahoo require, and what we do for you.", fi: "Mitä laki ja Gmail/Yahoo vaativat, ja mitä teemme puolestasi." },
    body: {
      en: `## What we do automatically
- Every email carries **List-Unsubscribe** headers; single-recipient mail gets **one-click** unsubscribe (RFC 8058), which Gmail and Yahoo require for bulk senders.
- Campaigns add a visible unsubscribe link if you did not place \`{{unsubscribe_url}}\` yourself.
- Unsubscribes are added to the suppression list at once and fire \`email.unsubscribed\`.

## What you are responsible for
- **Consent**: under GDPR and the ePrivacy rules, marketing email needs prior consent (or, for existing customers, a similar-products soft opt-in with an easy opt-out).
- **Identity**: say who you are; include your company name and address in marketing mail.
- **Honesty**: subjects must not mislead.
- **Records**: keep when and how each person signed up.

## Data
Contacts and messages stay in your workspace. Delete contacts any time; removing an audience removes its contacts.

> For bulk marketing, send one email per recipient (campaigns already do) so one-click unsubscribe works.`,
      fi: `## Mitä teemme automaattisesti
- Jokaisessa viestissä on **List-Unsubscribe**-otsakkeet; yhden vastaanottajan viestit saavat **yhden klikkauksen** perumisen (RFC 8058), jota Gmail ja Yahoo vaativat massapostittajilta.
- Kampanjat lisäävät näkyvän perumislinkin, jos et lisännyt \`{{unsubscribe_url}}\`-muuttujaa itse.
- Perumiset lisätään estolistalle heti ja ne laukaisevat \`email.unsubscribed\`-tapahtuman.

## Mistä sinä vastaat
- **Suostumus**: GDPR:n ja sähköisen viestinnän sääntöjen mukaan markkinointisähköposti vaatii ennakkosuostumuksen (tai nykyisille asiakkaille vastaavia tuotteita koskevan markkinoinnin helpolla kieltäytymismahdollisuudella).
- **Tunnistettavuus**: kerro kuka olet; lisää yrityksesi nimi ja osoite markkinointiviesteihin.
- **Rehellisyys**: otsikot eivät saa johtaa harhaan.
- **Kirjanpito**: säilytä tieto siitä, milloin ja miten kukin liittyi listalle.

## Tiedot
Yhteystiedot ja viestit pysyvät työtilassasi. Poista yhteystietoja milloin tahansa; yleisön poisto poistaa sen yhteystiedot.

> Massamarkkinoinnissa lähetä yksi viesti vastaanottajaa kohden (kampanjat tekevät näin), jotta yhden klikkauksen peruminen toimii.`,
    },
  },
  {
    slug: "limits", cat: "account", min: 4,
    title: { en: "Sandbox, limits and quotas", fi: "Hiekkalaatikko, rajat ja kiintiöt" },
    summary: { en: "How much you can send and how to raise it.", fi: "Kuinka paljon voit lähettää ja miten rajaa nostetaan." },
    body: {
      en: `## Sandbox
New accounts can send live email only to verified members of their own team. Upgrade to a paid plan, or ask support to approve the account, to send to anyone.

## Monthly and daily quotas
Your plan sets a monthly number of live emails (Free: 1,000). Test-key emails do not count. You get an email at 80% and 100% of the monthly limit.

## Rate limits
Per-second, per-minute and per-hour limits protect deliverability. Over the limit, the API returns 429 with \`Retry-After\`; campaigns slow down and continue on their own.

## When a campaign pauses
If the daily or monthly quota runs out mid-campaign, it pauses with the reason shown. Resume it after the quota resets or after upgrading.

See [Pricing](/pricing) for plan limits.`,
      fi: `## Hiekkalaatikko
Uudet tilit voivat lähettää oikeaa postia vain oman tiiminsä vahvistetuille jäsenille. Päivitä maksulliseen pakettiin tai pyydä tukea hyväksymään tili lähettääksesi kenelle tahansa.

## Kuukausi- ja päiväkiintiöt
Paketti määrää kuukausittaisten oikeiden viestien määrän (Free: 1 000). Testiavaimen viestit eivät lasketa. Saat sähköpostin 80 %:n ja 100 %:n kohdalla.

## Nopeusrajat
Sekunti-, minuutti- ja tuntirajat suojaavat toimitettavuutta. Rajan ylittyessä API palauttaa 429 ja \`Retry-After\`; kampanjat hidastavat ja jatkavat itse.

## Kun kampanja keskeytyy
Jos päivä- tai kuukausikiintiö loppuu kesken kampanjan, se keskeytyy ja syy näytetään. Jatka kampanjaa kiintiön nollauduttua tai paketin päivityksen jälkeen.

Katso pakettien rajat [Hinnoittelusta](/pricing).`,
    },
  },
  {
    slug: "team-security", cat: "account", min: 4,
    title: { en: "Team roles and account security", fi: "Tiimiroolit ja tilin tietoturva" },
    summary: { en: "Who can do what, and how to keep keys safe.", fi: "Kuka voi tehdä mitä ja miten avaimet pidetään turvassa." },
    body: {
      en: `## Roles
- **Owner**: everything, including billing and deleting the workspace.
- **Admin**: domains, webhooks, team, suppressions.
- **Developer**: API keys, templates, contacts and campaigns.
- **Read-only**: can view, cannot change.
Roles are enforced on the server for every request.

## Keep keys safe
- Store keys in environment variables or a secrets manager, never in code or the browser.
- Use separate keys per system and the smallest scopes.
- Revoke a key the moment it might have leaked; revoking takes effect immediately.

## Audit log
Every key, domain, role, webhook, campaign and sending action is recorded under **Audit log**.

> Repeated failed logins or API key attempts from one network are blocked automatically.`,
      fi: `## Roolit
- **Omistaja**: kaikki, myös laskutus ja työtilan poisto.
- **Ylläpitäjä**: verkkotunnukset, webhookit, tiimi, estolista.
- **Kehittäjä**: API-avaimet, mallipohjat, yhteystiedot ja kampanjat.
- **Vain luku**: voi katsoa, ei muuttaa.
Roolit tarkistetaan palvelimella jokaisessa pyynnössä.

## Pidä avaimet turvassa
- Säilytä avaimet ympäristömuuttujissa tai salaisuuksien hallinnassa, ei koskaan koodissa tai selaimessa.
- Käytä erillisiä avaimia järjestelmittäin ja pienimpiä oikeuksia.
- Peru avain heti, jos se on voinut vuotaa; peruminen toimii välittömästi.

## Tapahtumaloki
Jokainen avain-, verkkotunnus-, rooli-, webhook-, kampanja- ja lähetystoiminto kirjataan **Tapahtumalokiin**.

> Toistuvat epäonnistuneet kirjautumiset tai API-avainyritykset samasta verkosta estetään automaattisesti.`,
    },
  },
];
